import { supabase } from './supabase'
import type {
  AdminData,
  AdminDashboardPayload,
  CatalogPayload,
  HomePayload,
  ProductDetail,
  OrderStatus,
  CartQuote,
  NavigationLink,
  EfiAdminSettings,
  EfiPublicSettings,
  EfiPaymentMethod,
  EfiCardBrand,
  EfiInstallmentQuote
} from '../types/commerce'

const unwrap = <T>(data: T | null, error: { message: string } | null): T => {
  if (error) throw new Error(error.message)
  if (data === null) throw new Error('Resposta vazia do servidor.')
  return data
}

export const storefrontApi = {
  async home() {
    const { data, error } = await supabase.rpc('get_storefront_home')
    return unwrap(data as HomePayload | null, error)
  },
  async navigation() {
    const { data, error } = await supabase.rpc('get_storefront_navigation')
    return unwrap(data as NavigationLink[] | null, error)
  },
  async catalog(params: {
    category?: string | null
    collection?: string | null
    gender?: string | null
    search?: string | null
    sort?: 'newest' | 'price_asc' | 'price_desc' | 'featured'
    limit?: number
    offset?: number
  } = {}) {
    const { data, error } = await supabase.rpc('get_catalog', {
      p_category_slug: params.category ?? null,
      p_collection_slug: params.collection ?? null,
      p_gender: params.gender ?? null,
      p_search: params.search ?? null,
      p_sort: params.sort ?? 'newest',
      p_limit: params.limit ?? 24,
      p_offset: params.offset ?? 0
    })
    return unwrap(data as CatalogPayload | null, error)
  },
  async product(slug: string) {
    const { data, error } = await supabase.rpc('get_product', { p_slug: slug })
    return unwrap(data as ProductDetail | null, error)
  },
  async quoteCart(items: Array<{ variant_id: string; quantity: number }>) {
    const { data, error } = await supabase.rpc('quote_cart', { p_items: items })
    return unwrap(data as CartQuote | null, error)
  },
  async checkoutSettings() {
    const { data, error } = await supabase.rpc('efi_public_settings')
    return unwrap(data as EfiPublicSettings | null, error)
  },
  async quoteCheckout(items: Array<{ variant_id: string; quantity: number }>, method: EfiPaymentMethod) {
    const { data, error } = await supabase.rpc('quote_cart_checkout', { p_items: items, p_method: method })
    return unwrap(data as CartQuote | null, error)
  },
  async efiInstallments(items: Array<{ variant_id: string; quantity: number }>, brand: EfiCardBrand) {
    const { data, error } = await supabase.functions.invoke('efi-installments', {
      body: { items, brand }
    })
    if (error || !data || data.source !== 'efi_api' || !Array.isArray(data.installments)) {
      throw new Error('A Efí não retornou as condições de parcelamento.')
    }
    return data as EfiInstallmentQuote
  },
  async placeOrderCheckout(payload: unknown, method: EfiPaymentMethod) {
    const { data, error } = await supabase.rpc('place_order_checkout', { p_payload: payload, p_method: method })
    return unwrap(data as {
      id: string; order_number: number; status: OrderStatus;
      subtotal: number; shipping_amount: number; payment_fee: number; total: number;
      payment_method: EfiPaymentMethod; checkout_token: string
    } | null, error)
  },
  async orderPaymentState(id: string, checkoutToken: string) {
    const { data, error } = await supabase.rpc('get_order_payment_state', { p_order_id: id, p_checkout_token: checkoutToken })
    return unwrap(data as { payment_status: string; total: number; payment_fee: number; payment_method: string; status: OrderStatus } | null, error)
  },
  async placeOrder(payload: unknown) {
    const { data, error } = await supabase.rpc('place_order', { p_payload: payload })
    return unwrap(data as { id: string; order_number: number; status: OrderStatus; subtotal: number; shipping_amount: number; total: number; checkout_token: string } | null, error)
  }
}

export const adminApi = {
  async isAdmin() {
    const { data, error } = await supabase.rpc('is_admin')
    if (error) return false
    return data === true
  },
  async testEfiConnection() {
    const { data: authData, error: authError } = await supabase.auth.getSession()
    if (authError || !authData.session?.access_token) {
      throw new Error('Sua sessão expirou. Saia do administrador e entre novamente.')
    }
    const { data, error } = await supabase.functions.invoke('efi-test-connection', {
      body: {},
      headers: { Authorization: `Bearer ${authData.session.access_token}` }
    })
    if (error) {
      // Erros HTTP das funções contêm uma mensagem segura; não escondê-los
      // atrás de uma falha genérica de credenciais.
      const response = (error as { context?: Response }).context
      if (response && typeof response.json === 'function') {
        const payload = await response.json().catch(() => null) as { error?: string } | null
        if (payload?.error) throw new Error(payload.error)
        if (response.status === 401) throw new Error('Sessão não autorizada. Saia e entre novamente no administrador.')
        if (response.status === 403) throw new Error('Seu usuário não tem permissão para validar a conta Efí.')
        if (response.status === 502) throw new Error('A Efí recusou a autenticação. Confira se as credenciais são do ambiente selecionado.')
      }
      throw new Error('Não foi possível conectar ao servidor da Efí. Verifique sua conexão e tente atualizar o painel.')
    }
    if (!data?.ok) throw new Error(data?.error || 'Conexão não validada.')
    return data as {
      ok: true
      environment: string
      installment_source: 'efi_api'
      consulted_at: string
      example_brand: 'visa'
      example_total: number
      installments?: Array<{
        number: number
        monthly_amount: number
        total: number
        has_interest: boolean
        interest_percent: number
      }>
      installments_warning?: string
    }
  },
  async getEfiSettings() {
    const { data, error } = await supabase.rpc('admin_efi_get_settings')
    return unwrap(data as EfiAdminSettings | null, error)
  },
  async saveEfiSettings(payload: Record<string, unknown>) {
    const { data, error } = await supabase.rpc('admin_efi_save_settings', { p_payload: payload })
    return unwrap(data as EfiAdminSettings | null, error)
  },
  async dashboard() {
    const { data, error } = await supabase.rpc('get_admin_dashboard')
    return unwrap(data as AdminDashboardPayload | null, error)
  },
  async data() {
    const { data, error } = await supabase.rpc('get_admin_data')
    return unwrap(data as AdminData | null, error)
  },
  async deleteProduct(id: string): Promise<'deleted' | 'archived'> {
    const { data, error } = await supabase.rpc('admin_delete_product', { p_id: id })
    if (error) throw new Error(error.message)
    if (data !== 'deleted' && data !== 'archived') throw new Error('Resposta de exclusão inválida.')
    return data
  },
  async generateVariantSku() {
    const { data, error } = await supabase.rpc('admin_generate_variant_sku')
    return unwrap(data as string | null, error)
  },
  async saveProduct(payload: unknown) {
    const { data, error } = await supabase.rpc('admin_save_product', { p_payload: payload })
    return unwrap(data as string | null, error)
  },
  async saveCategory(payload: unknown) {
    const { data, error } = await supabase.rpc('admin_save_category', { p_payload: payload })
    return unwrap(data as string | null, error)
  },
  async saveCollection(payload: unknown) {
    const { data, error } = await supabase.rpc('admin_save_collection', { p_payload: payload })
    return unwrap(data as string | null, error)
  },
  async removeCategory(id: string): Promise<'deleted' | 'archived'> {
    const { data, error } = await supabase.rpc('admin_remove_category', { p_id: id })
    if (error) throw new Error(error.message)
    if (data !== 'deleted' && data !== 'archived') throw new Error('Resposta inválida ao remover a categoria.')
    return data
  },
  async removeCollection(id: string): Promise<'deleted' | 'archived'> {
    const { data, error } = await supabase.rpc('admin_remove_collection', { p_id: id })
    if (error) throw new Error(error.message)
    if (data !== 'deleted' && data !== 'archived') throw new Error('Resposta inválida ao remover a coleção.')
    return data
  },
  async saveBanner(payload: unknown) {
    const { data, error } = await supabase.rpc('admin_save_banner', { p_payload: payload })
    return unwrap(data as string | null, error)
  },
  async saveStoreWhatsApp(whatsapp: string) {
    const { data, error } = await supabase.rpc('admin_save_store_whatsapp', { p_whatsapp: whatsapp })
    if (error) throw new Error(error.message)
    return data as string | null
  },
  async saveNavigationLink(payload: unknown) {
    const { data, error } = await supabase.rpc('admin_save_navigation_link', { p_payload: payload })
    return unwrap(data as string | null, error)
  },
  async deleteNavigationLink(id: string) {
    const { data, error } = await supabase.rpc('admin_delete_navigation_link', { p_id: id })
    return unwrap(data as boolean | null, error)
  },
  async setOrderStatus(orderId: string, status: OrderStatus) {
    const { data, error } = await supabase.rpc('admin_set_order_status', { p_order_id: orderId, p_status: status })
    return unwrap(data as { id: string; status: OrderStatus } | null, error)
  }
}
